import { useMutation, useQuery, useQueryClient } from '@tanstack/solid-query'
import { createSignal, Show } from 'solid-js'
import { currencyArt } from '~/assets/rewards'
import { BottomCta } from '~/components/bottom-cta'
import { ConnectionIssueDialog } from '~/components/connection-issue-dialog'
import { Faq } from '~/components/faq'
import { HeroSection } from '~/components/hero-section'
import { HowItWorks } from '~/components/how-it-works'
import { InvitedPill } from '~/components/invited-pill'
import { LoadError } from '~/components/load-error'
import { ProgressCard } from '~/components/progress-card'
import { RewardReveal } from '~/components/reward-reveal'
import { TopNav } from '~/components/top-nav'
import { deriveRevealContent, type RevealContent } from '~/lib/delivery'
import {
  deriveMilestoneViews,
  type MilestoneView,
  withClaimedReward,
} from '~/lib/milestones'
import { nominalReward } from '~/lib/reward-visuals'
import {
  claimErrorKind,
  claimMilestoneRewardAsUser,
  referralQueries,
  type ReferralRewards,
} from '~/services/referral'
import { userQueries } from '~/services/user'

export function App() {
  const queryClient = useQueryClient()
  const code = useQuery(() => referralQueries.code())
  const progress = useQuery(() => referralQueries.progress())
  const milestones = useQuery(() => referralQueries.milestones())
  const rewards = useQuery(() => referralQueries.rewards())
  // Fail-soft: on error the pill just degrades to its no-avatar variant.
  const invitees = useQuery(() => referralQueries.invitees())

  const accumulatedScore = () => progress.data?.accumulated_score ?? 0
  const views = () =>
    deriveMilestoneViews(
      milestones.data?.steps ?? [],
      accumulatedScore(),
      rewards.data?.rewards ?? [],
    )

  // The reveal opens only after the claim call delivered the reward (see
  // CONTEXT.md "Reward reveal"); it presents the delivery, hybrid-derived.
  const [reveal, setReveal] = createSignal<RevealContent | null>(null)
  const [claimIssueOpen, setClaimIssueOpen] = createSignal(false)
  // The claimed milestone whose rewards-cache append is parked until the
  // reveal closes: appending on success would flip the row to its claimed
  // tick behind the full-screen reveal, so the tick's pop-in would already
  // have played by the time the page is visible again.
  let pendingClaimed: MilestoneView | undefined

  const claim = useMutation(() => ({
    mutationFn: async (milestone: MilestoneView) => {
      // Snapshot the pre-claim balance before the delivery credits it
      // server-side — the reveal's pills count up from this cached value
      // (see userQueries.currency). A failed fetch blocks the claim (the
      // reward request never fires); rethrown as a plain error so a coin/gem
      // HTTP status can't be misread as a stale-claim answer in onError.
      await queryClient.ensureQueryData(userQueries.currency()).catch(() => {
        throw new Error('pre-claim balance fetch failed')
      })
      return claimMilestoneRewardAsUser(milestone.requiredScore)
    },
    onSuccess: (delivery, milestone) => {
      pendingClaimed = milestone
      setReveal(
        deriveRevealContent(
          delivery,
          nominalReward(milestone.index),
          currencyArt,
        ),
      )
    },
    onError: (error) => {
      if (claimErrorKind(error) === 'other') {
        setClaimIssueOpen(true)
        return
      }
      // Stale local state — already claimed elsewhere (409) or the rendered
      // score is out of date (404). Refetching corrects the milestone states
      // silently; no dialog.
      void queryClient.invalidateQueries({
        queryKey: referralQueries.rewards().queryKey,
      })
      void queryClient.invalidateQueries({
        queryKey: referralQueries.progress().queryKey,
      })
    },
  }))

  const handleClaim = (milestone: MilestoneView) => {
    if (milestone.state !== 'claimable' || claim.isPending) return
    claim.mutate(milestone)
  }

  const closeReveal = () => {
    const milestone = pendingClaimed
    pendingClaimed = undefined
    if (milestone) {
      // Local append, not invalidate: present-in-log ≡ claimed, and a
      // refetch race would wash the claim away.
      queryClient.setQueryData<ReferralRewards>(
        referralQueries.rewards().queryKey,
        (prev) => ({
          rewards: withClaimedReward(prev?.rewards ?? [], {
            required_score: milestone.requiredScore,
            claimed_at: new Date().toISOString(),
          }),
        }),
      )
    }
    setReveal(null)
  }
  const claimingScore = () =>
    claim.isPending ? claim.variables?.requiredScore : undefined

  const loadFailed = () =>
    code.isError || progress.isError || milestones.isError || rewards.isError
  const retryLoad = () => {
    for (const query of [code, progress, milestones, rewards]) {
      if (query.isError) void query.refetch()
    }
    if (invitees.isError) void invitees.refetch()
  }

  return (
    <main class="relative min-h-screen min-w-[320px] overflow-x-hidden bg-page">
      <TopNav />

      <Show when={!loadFailed()} fallback={<LoadError onRetry={retryLoad} />}>
        <div class="mx-auto flex max-w-[640px] flex-col gap-[18px] pb-[180px] pt-[var(--safe-area-top)]">
          <HeroSection />

          <div class="flex flex-col gap-4 px-[30px]">
            <InvitedPill
              count={progress.data?.invite_count ?? 0}
              avatars={invitees.data?.avatars}
            />
            <div class="mt-[-26px]">
              <ProgressCard
                milestones={views()}
                accumulatedScore={accumulatedScore()}
                claimingScore={claimingScore()}
                onClaim={handleClaim}
              />
            </div>
          </div>

          <div class="flex flex-col gap-4 px-[30px]">
            <HowItWorks />
            <Faq />
          </div>
        </div>

        <BottomCta code={code.data?.code ?? ''} />
      </Show>

      <ConnectionIssueDialog
        open={claimIssueOpen()}
        onClose={() => setClaimIssueOpen(false)}
      />

      <Show when={reveal()}>
        {(content) => (
          <RewardReveal content={content()} onClose={closeReveal} />
        )}
      </Show>
    </main>
  )
}
